import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Button, Divider, Grid, Header, Icon, Popup, Segment } from "semantic-ui-react";
import {
  getPencilerStartProjectsStatus,
  refreshOrganization
} from "../../actions/accounts/accounts";
import {
  getStartProjectProduct,
  purchaseStartProjectsCheckoutRedirect,
  setOpenPurchaseStartProjectModalStatus
} from "../../actions/products/products";
import {
  addSubscriptionCheckoutRedirect,
  getCurrentPencilerSubscription,
  getProrationCost,
  getSubscriptionOptions,
  upgradeStripePencilerSubscription
} from "../../actions/subscriptions/subscriptions";
import { AppDispatch, RootState } from "../../reducers";
import {
  setAddSubscriptionModalStatus,
  setUpgradeSubscriptionModalStatus
} from "../../store/subscriptions";
import { exists } from "../../utils/utils";
import CustomLoader from "../common/CustomLoader";
import CustomSuccess from "../common/CustomSuccess";
import AddStartProjectsModal from "../products/AddStartProjectsModal";
import SelectSubscriptionModal from "./SelectSubscriptionModal";

const mapStateToProps = (state: RootState) => {
  return {
    organization: state.accounts.organization,
    user: state.users.userClaims,
    startProjects: state.accounts.pencilerStartProjectsStatus,
    gettingStartProjects: state.accounts.gettingPencilerStartProjectsStatus,
    getStartProjectsError: state.accounts.getPencilerStartProjectsStatusError,
    currentPencilerSubscription: state.subscriptions.currentPencilerSubscription,
    gettingCurrentPencilerSubscription: state.subscriptions.gettingCurrentPencilerSubscription,
    getCurrentPencilerSubscriptionSuccess:
      state.subscriptions.getCurrentPencilerSubscriptionSuccess,
    getCurrentPencilerSubscriptionError: state.subscriptions.getCurrentPencilerSubscriptionError,
    getCurrentPencilerSubscriptionErrorMessage:
      state.subscriptions.getCurrentPencilerSubscriptionErrorMessage,
    subscriptionOptions: state.subscriptions.subscriptionOptions,
    gettingSubscriptionOptions: state.subscriptions.gettingSubscriptionOptions,
    gettingSubscriptionOptionsSuccess: state.subscriptions.gettingSubscriptionOptionsSuccess,
    gettingSubscriptionOptionsError: state.subscriptions.gettingSubscriptionOptionsError,
    gettingSubscriptionOptionsErrorMessage:
      state.subscriptions.gettingSubscriptionOptionsErrorMessage,
    openAddSubscriptionModal: state.subscriptions.openAddSubscriptionModal,
    submittingAddSubscription: state.subscriptions.submittingAddSubscription,
    addSubscriptionModalFailed: state.subscriptions.addSubscriptionModalFailed,
    addSubscriptionModalFailedMessage: state.subscriptions.addSubscriptionModalFailedMessage,
    openUpgradeSubscriptionModal: state.subscriptions.openUpgradeSubscriptionModal,
    submittingUpgradeSubscription: state.subscriptions.submittingUpgradeSubscription,
    upgradeSubscriptionModalFailed: state.subscriptions.upgradeSubscriptionModalFailed,
    upgradeSubscriptionModalFailedMessage:
      state.subscriptions.upgradeSubscriptionModalFailedMessage,
    startProject: state.products.startProject,
    gettingStartProject: state.products.gettingStartProject,
    getStartProjectSuccess: state.products.getStartProjectSuccess,
    getStartProjectError: state.products.getStartProjectError,
    getStartProjectErrorMessage: state.products.getStartProjectErrorMessage,
    openPurchaseStartProjectModal: state.products.openPurchaseStartProjectModal,
    submittingPurchaseStartProject: state.products.submittingPurchaseStartProject,
    purchaseStartProjectFailed: state.products.purchaseStartProjectFailed,
    purchaseStartProjectFailedMessage: state.products.purchaseStartProjectFailedMessage,
    prorationToken: state.subscriptions.prorationToken,
    prorationCost: state.subscriptions.prorationCost,
    gettingProrationCost: state.subscriptions.gettingProrationCost,
    showSuccessMessage: state.statusMessages.showSuccessMessage,
    submitSucceededMessageHeader: state.statusMessages.submitSucceededMessageHeader,
    submitSucceededMessageContent: state.statusMessages.submitSucceededMessageContent
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  addSubscriptionCheckoutRedirect: (subscriptionStripeId: string) =>
    dispatch(addSubscriptionCheckoutRedirect(subscriptionStripeId)),
  setAddSubscriptionModalStatus: (isOpen: boolean) =>
    dispatch(setAddSubscriptionModalStatus(isOpen)),
  setUpgradeSubscriptionModalStatus: (isOpen: boolean) =>
    dispatch(setUpgradeSubscriptionModalStatus(isOpen)),
  setOpenPurchaseStartProjectModalStatus: (isOpen: boolean) =>
    dispatch(setOpenPurchaseStartProjectModalStatus(isOpen)),
  purchaseStartProjectsCheckoutRedirect: (quantity: number) =>
    dispatch(purchaseStartProjectsCheckoutRedirect(quantity)),
  getSubscriptionOptions: () => dispatch(getSubscriptionOptions()),
  getPencilerStartProjectsStatus: () => dispatch(getPencilerStartProjectsStatus()),
  getStartProjectProduct: () => dispatch(getStartProjectProduct()),
  refreshOrganization: () => dispatch(refreshOrganization()),
  getProrationCost: (planId: string) => dispatch(getProrationCost(planId)),
  upgradeStripePencilerSubscription: (planId: string, prorationToken: string | null) =>
    dispatch(upgradeStripePencilerSubscription(planId, prorationToken)),
  getCurrentPencilerSubscription: () => dispatch(getCurrentPencilerSubscription())
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type SubscriptionManagementProps = {} & PropsFromRedux;
type SubscriptionManagementState = { rolesPermissionsKey: number };

/**
 * List and manage account/organization users.
 * Some options may only appear based on the user's role.
 */
class SubscriptionManagement extends React.Component<
  SubscriptionManagementProps,
  SubscriptionManagementState
> {
  constructor(props: SubscriptionManagementProps) {
    super(props);
    props.getCurrentPencilerSubscription();
  }

  /**
   * Remap subscription options to a valid array
   * @private
   */
  _remapSubscriptionOptions = () =>
    // @ts-ignore until typing subscriptionOptions slice
    this.props.subscriptionOptions.map(option => ({
      key: option.id,
      text: option.title,
      value: option.stripePlanId,
      disabled:
        //@ts-ignore until typing exists
        exists(this.props.organization.pencilerSubscription) &&
        //@ts-ignore until typing exists
        exists(this.props.organization.pencilerSubscription.subscriptionOption) &&
        option.stripePlanId ===
          //@ts-ignore until typing exists
          this.props.organization.pencilerSubscription.subscriptionOption.stripePlanId
    }));

  /**
   * Triggers add subscription modal while requesting subscription options.
   *
   * @private
   */
  _addSubscriptionClick = () => {
    this.props.getSubscriptionOptions();
    this.props.setAddSubscriptionModalStatus(true);
  };

  /**
   * Triggers upgrade subscription modal while requesting subscription options.
   *
   * @private
   */
  _upgradeSubscriptionClick = () => {
    this.props.getSubscriptionOptions();
    this.props.setUpgradeSubscriptionModalStatus(true);
  };

  /**
   * Triggers add start projects modal while requesting start project product.
   *
   * @private
   */
  _addStartProjectsClick = () => {
    this.props.getStartProjectProduct();
    this.props.setOpenPurchaseStartProjectModalStatus(true);
  };

  /**
   * Handles add subscription form submit.
   * Triggers call to API and updates status with Redux Actions.
   * @param {Array<Object>} values          - Form values
   * @private
   */
  _handleAddSubscriptionFormSubmit = (values: any) => {
    this.props.addSubscriptionCheckoutRedirect(values.selectedSubscription);
  };

  /**
   * Handles start projects form submit.
   * Triggers call to API and updates status with Redux Actions.
   * @param {Array<Object>} values          - Form values
   * @private
   */
  _handleAddStartProjectsFormSubmit = (values: any) => {
    this.props.purchaseStartProjectsCheckoutRedirect(values.startProjectsQuantity);
  };

  /**
   * Handles upgrade subscription form submit.
   * Triggers call to API and updates status with Redux Actions.
   * @param {Array<Object>} values          - Form values
   * @private
   */
  _handleUpgradeSubscriptionFormSubmit = (values: any) =>
    this.props.upgradeStripePencilerSubscription(
      values.selectedSubscription,
      this.props.prorationToken
    );

  /**
   * Decide if an user can purchase single projects based on it's current subscription.
   *
   * @return {boolean}        - True means disable.
   * @private
   */
  _disableSingleProjectPurchase = () =>
    exists(this.props.currentPencilerSubscription) &&
    exists(this.props.currentPencilerSubscription.subscriptionOption) &&
    this.props.currentPencilerSubscription.subscriptionOption.isUnlimited === true;

  _renderAddOrUpgradeSubscription = () => {
    //@ts-ignore until typing exists
    if (exists(this.props.organization.pencilerSubscription)) {
      return (
        <Grid.Column>
          <Header icon>
            <Icon name="calendar check outline" />
            Upgrade Subscription
          </Header>
          <Button primary onClick={this._upgradeSubscriptionClick}>
            Upgrade
          </Button>
        </Grid.Column>
      );
    }
    return (
      <Grid.Column>
        <Header icon>
          <Icon name="calendar plus outline" />
          Add Subscription
        </Header>
        <Button primary onClick={this._addSubscriptionClick}>
          Add
        </Button>
      </Grid.Column>
    );
  };

  render() {
    if (this.props.showSuccessMessage) {
      return (
        <CustomSuccess
          header={this.props.submitSucceededMessageHeader}
          content={this.props.submitSucceededMessageContent}
          isModal={true}
        />
      );
    }
    if (this.props.gettingCurrentPencilerSubscription) {
      return <CustomLoader isModal={true} />;
    }
    return (
      <div>
        {this.props.openAddSubscriptionModal ? (
          <SelectSubscriptionModal
            isLoading={
              this.props.gettingSubscriptionOptions || this.props.submittingAddSubscription
            }
            formSubmitFailed={this.props.addSubscriptionModalFailed}
            formSubmitFailedMessage={this.props.addSubscriptionModalFailedMessage}
            subscriptionOptions={this._remapSubscriptionOptions()}
            openModal={this.props.openAddSubscriptionModal}
            onModalClose={() => this.props.setAddSubscriptionModalStatus(false)}
            onSubmit={(values: any) => this._handleAddSubscriptionFormSubmit(values)}
          />
        ) : null}
        {this.props.openUpgradeSubscriptionModal ? (
          <SelectSubscriptionModal
            isLoading={
              this.props.gettingSubscriptionOptions ||
              this.props.submittingUpgradeSubscription ||
              this.props.gettingProrationCost
            }
            formSubmitFailed={this.props.upgradeSubscriptionModalFailed}
            formSubmitFailedMessage={this.props.upgradeSubscriptionModalFailedMessage}
            subscriptionOptions={this._remapSubscriptionOptions()}
            defaultSubscription={
              //@ts-ignore
              this.props.organization.pencilerSubscription?.subscriptionOption.stripePlanId
            }
            openModal={this.props.openUpgradeSubscriptionModal}
            onModalClose={() => this.props.setUpgradeSubscriptionModalStatus(false)}
            onSubmit={(values: any) => this._handleUpgradeSubscriptionFormSubmit(values)}
            isUpgrading={true}
            prorationCost={this.props.prorationCost}
            gettingProrationCost={this.props.gettingProrationCost}
            calculateProrationCost={this.props.getProrationCost}
          />
        ) : null}
        {this.props.openPurchaseStartProjectModal ? (
          <AddStartProjectsModal
            isLoading={this.props.gettingStartProject || this.props.submittingPurchaseStartProject}
            formSubmitFailed={this.props.purchaseStartProjectFailed}
            formSubmitFailedMessage={this.props.purchaseStartProjectFailedMessage}
            openModal={this.props.openPurchaseStartProjectModal}
            onModalClose={() => this.props.setOpenPurchaseStartProjectModalStatus(false)}
            onSubmit={(values: any) => this._handleAddStartProjectsFormSubmit(values)}
          />
        ) : null}
        <Grid textAlign="center" style={{ height: "100%" }} verticalAlign="top">
          <Grid.Row>
            <Segment placeholder style={{ width: "100%", maxWidth: 750 }}>
              <Grid columns={2} stackable textAlign="center">
                <Divider vertical>Or</Divider>
                <Grid.Row verticalAlign="middle">
                  {this._renderAddOrUpgradeSubscription()}
                  <Grid.Column>
                    <Header icon>
                      <Icon name="building" />
                      Add Single Project
                    </Header>
                    <Popup
                      hoverable
                      flowing
                      content="You already hold an unlimited subscription!"
                      disabled={!this._disableSingleProjectPurchase()}
                      position="bottom center"
                      trigger={
                        <span>
                          <Button
                            primary
                            onClick={() => this._addStartProjectsClick()}
                            disabled={this._disableSingleProjectPurchase()}
                          >
                            Add
                          </Button>
                        </span>
                      }
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          </Grid.Row>
        </Grid>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SubscriptionManagement);
